import { defineStore } from "pinia"
import { useCurrentAccountStore } from "./currentAccount"
import { PaymentScheduleService } from "@/services/PaymentScheduleService"
import { ITariff, IUnitRates } from "@/models/tariff/tariff.types"
import { ITariffDetails } from "@/models/tariffDetails/tariffDetails.types"
import { TariffDetails } from "@/models/tariffDetails/tariffDetails"
import { ITariffCompared } from "@/models/tariffCompared/tariffCompared.types"
import { TariffCompared } from "@/models/tariffCompared/tariffCompared"
import {
  ElectricityVatChange,
  NO_ELECTRICITY_VAT_CHANGE,
  rebaseElectricityTariff,
  rebaseUnitRates,
  rebaseVatInclusiveRate,
} from "@/helpers/vatHelper"
import {
  SoDataTableColumnType,
  SoDataTableHeader,
  SoDataTableItem,
} from "@/types/components/SoDataTable"
import { MeterpointTariffs } from "@/types/MeterpointTariffs"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { IRegister } from "@/models/meterRegister/meterRegister.types"
import tariffHelper, { RenewalStartAlignmentType } from "@/helpers/tariffHelper"
import { formatMoney, formatPriceInPence } from "@/helpers/moneyHelpers"
import dayjs, { Dayjs } from "dayjs"
import minMax from "dayjs/plugin/minMax"
import { RemoveReadonly } from "@/helpers/typeHelper"
import { FeaturesService } from "@/services/admin/FeaturesService"
import { renewalDateFormat } from "@/helpers/dateHelper"
import { DEFAULT_RENEWAL_WINDOW_PERIOD } from "@/constants/finance"

dayjs.extend(minMax)

// Fresh model instances, so re-initialising cannot compound the adjustment.
const rebaseElectricityTariffDetails = (
  details: ITariffDetails,
  oldRate: number,
  newRate: number,
): ITariffDetails => {
  if (details.isGas) return details

  const unitRates = details.unitRates
    ? rebaseUnitRates(details.unitRates, oldRate, newRate)
    : (details.unitRates as unknown as IUnitRates)
  const unitRate =
    details.unitRate == null
      ? undefined
      : rebaseVatInclusiveRate(details.unitRate, oldRate, newRate)

  return new TariffDetails(
    details.code,
    details.gspGroup,
    details.displayName,
    rebaseVatInclusiveRate(details.standingCharge, oldRate, newRate),
    rebaseVatInclusiveRate(details.earlyTerminationCharge, oldRate, newRate),
    unitRates,
    unitRate,
  )
}

// Both months are re-based by the same factor, preserving hasDifferentUnitRates.
const rebaseElectricityComparison = (
  comparison: ITariffCompared,
  oldRate: number,
  newRate: number,
): ITariffCompared =>
  new TariffCompared(
    comparison.thisMonth
      ? rebaseElectricityTariffDetails(comparison.thisMonth, oldRate, newRate)
      : comparison.thisMonth,
    comparison.nextMonth
      ? rebaseElectricityTariffDetails(comparison.nextMonth, oldRate, newRate)
      : comparison.nextMonth,
  )

// getTariffMonthlyRates exposes no VAT rate, so the comparison follows the rate
// its own product — the current tariff — reports.
const rebaseMonthlyComparison = (
  comparison: ITariffCompared | undefined,
  currentTariff: ITariff | undefined,
  newRate: number,
): ITariffCompared | undefined => {
  const oldRate = currentTariff?.productDetails.vatRate
  return comparison && oldRate != null && oldRate !== newRate
    ? rebaseElectricityComparison(comparison, oldRate, newRate)
    : comparison
}

const applyElectricityVatChange = (
  meterpointTariffs: MeterpointTariffs,
  newRate: number,
): MeterpointTariffs =>
  Object.fromEntries(
    Object.entries(meterpointTariffs).map(([id, mpt]) => [
      id,
      {
        ...mpt,
        currentTariff: mpt.currentTariff
          ? rebaseElectricityTariff(mpt.currentTariff, newRate, true)
          : mpt.currentTariff,
        publiclyAvailableTariffList: mpt.publiclyAvailableTariffList?.map(
          (tariff) => rebaseElectricityTariff(tariff, newRate),
        ),
        smartPaygTariffs: mpt.smartPaygTariffs?.map((tariff) =>
          rebaseElectricityTariff(tariff, newRate),
        ),
        monthlyRateComparison: rebaseMonthlyComparison(
          mpt.monthlyRateComparison,
          mpt.currentTariff,
          newRate,
        ),
      },
    ]),
  ) as MeterpointTariffs

export interface IRenewalsStore {
  isLoaded: boolean
  selectedTariffs: Record<string, ITariff>
  changeDate: Date | null
  renewalPeriod: number
  maxRenewalDate: string
  minRenewalDate: string
  includeExitFee: boolean
  includeAccountBalance: boolean
  oneOffPayment: string
  meterpointTariffs: MeterpointTariffs
  selectedAgreementsCostTotal: number
  exitFeeTotal: number
  currentAgreementsCostTotal: number
  defaultRenewalStart: { date: Dayjs; alignedTo: RenewalStartAlignmentType }
  defaultRenewalStartFormatted: string
  adjustedAnnualCost: number
  tariffCostsDifference: number
  suggestedPaymentAmount: number
  paymentChangeDiff: number
  currentPayments: string | null
  currentPaymentsValue: number
  closestAgreementEndDate: Dayjs
  quoteTableData: { headers: SoDataTableHeader[]; items: SoDataTableItem[] }
  agreementEndDates: Dayjs[]
  selectedTariffsLatestFromDate: Dayjs
  annualCosts: {
    [meterpointId: string]: {
      currentAgreementCost: number
      selectedAgreementCost: number
      importTariffCosts: {
        [tariffId: number]: number
      }
    }
  }
  registerUsage: {
    [meterpointId: string]: {
      [registerId: string]: {
        rateName: string | null
        usage: string
      }
    }
  }
  registerUsageConverted: {
    [meterpointId: string]: {
      [registerId: string]: {
        rateName: string | null
        usage: number
      }
    }
  }
  formattedRegisterUsage: {
    [meterpointId: string]: string
  }
  quoteModalTariffTableData: {
    agreementsChanging: string[][]
    agreementsNotChanging: string[][]
    containsVariableTariff: boolean
  }
  initialiseMeterpoints: () => void
  showRateChangeBanner: boolean
}

export const useRenewalsStore = defineStore("renewals", {
  state: () => {
    return {
      isLoaded: false,
      selectedTariffs: {},
      changeDate: null,
      registerUsage: {},
      includeExitFee: true,
      includeAccountBalance: false,
      oneOffPayment: "",
      meterpointTariffs: {},
    } as IRenewalsStore
  },
  actions: {
    updateAgreement(meterpointId: string, tariffId: number | null) {
      if (!tariffId) {
        delete this.selectedTariffs[meterpointId]
        return
      }
      const tariffs =
        this.meterpointTariffs[meterpointId].publiclyAvailableTariffList

      const selectedTariff = tariffs?.find(
        (tariff: ITariff) => tariff.id === tariffId,
      )

      if (selectedTariff) {
        this.selectedTariffs[meterpointId] = selectedTariff
      }
    },
    initialiseMeterpoints(
      meterpointTariffs: MeterpointTariffs,
      electricityVatChange: ElectricityVatChange = NO_ELECTRICITY_VAT_CHANGE,
    ) {
      this.$reset()
      this.meterpointTariffs = electricityVatChange.active
        ? applyElectricityVatChange(
            meterpointTariffs,
            electricityVatChange.newRate,
          )
        : meterpointTariffs
      // Iterate the re-based map so the default selected tariff — which drives
      // the annual-cost/payment calc — reflects the VAT change, not the raw param.
      Object.entries(this.meterpointTariffs).forEach(
        ([meterpointId, meterpointTariffs]) => {
          const currentTariff = meterpointTariffs?.currentTariff
          const currentTariffRate = currentTariff?.isOneRate
          const currentTariffIsNSC = currentTariff?.isNSC
          const isEligibleTariff = (tariff) => {
            const tariffRate = tariff.isOneRate
            const isGreenTariff = tariff.display.includes("Green")
            const isVariable = tariff.isVariable
            const hasMatchingRate = currentTariffRate === tariffRate

            return (
              !tariff.isExclusive &&
              !tariff.isExport &&
              ((!isVariable && hasMatchingRate && !isGreenTariff) ||
                (isVariable && hasMatchingRate && isGreenTariff))
            )
          }

          const filteredMeterpointTariffs =
            meterpointTariffs.publiclyAvailableTariffList
              ?.filter(isEligibleTariff)
              ?.sort((a, b) => (a.createdAt?.isBefore(b.createdAt) ? 1 : -1))

          const hasFixedTariffs = filteredMeterpointTariffs?.some(
            (tariff) => !tariff.isVariable,
          )

          let eligibleTariff

          if (currentTariffIsNSC) {
            // If the current agreement is NSC, recommend an NSC tariff.
            eligibleTariff = filteredMeterpointTariffs?.find(
              (tariff: ITariff) => tariff.isNSC,
            )
          } else if (hasFixedTariffs) {
            // If there are fixed tariffs, recommend the most recent one with a matching rate.
            eligibleTariff = filteredMeterpointTariffs?.reduce(
              (mostRecent, tariff) =>
                !mostRecent || tariff.createdAt.isAfter(mostRecent.createdAt)
                  ? tariff
                  : mostRecent,
            )
          } else {
            // If no fixed tariffs, recommend a Green tariff.
            eligibleTariff = filteredMeterpointTariffs?.find((tariff) =>
              tariff.display.includes("Green"),
            )
          }

          if (meterpointTariffs.currentTariff) {
            this.selectedTariffs[meterpointId] =
              eligibleTariff || meterpointTariffs.currentTariff
          }

          const registers = meterpointTariffs.meterpoint.registers.reduce(
            (
              acc: {
                [registerId: string]: { rateName: string | null; usage: string }
              },
              { id, rateName, usage }: IRegister,
            ) => {
              acc[id] = { rateName, usage: usage.toString() }
              return acc
            },
            {},
          )

          this.registerUsage[meterpointId] = registers
        },
      )

      this.changeDate = this.closestAgreementEndDate.toDate()

      if (useCurrentAccountStore().account.data.balance < 0) {
        this.includeAccountBalance = true
      }

      this.isLoaded = true
    },
  },
  getters: {
    meterpoints(): Record<string, IMeterpoint> {
      const meterpointsArray = useCurrentAccountStore().meterpoints.data
      const meterpointsMap: Record<string, IMeterpoint> =
        meterpointsArray.reduce((result, meterpoint) => {
          result[meterpoint.identifier] = meterpoint
          return result
        }, {})

      return meterpointsMap
    },
    renewalPeriod() {
      const renewalPeriod = useCurrentAccountStore().renewalPeriod.data
      return (renewalPeriod || DEFAULT_RENEWAL_WINDOW_PERIOD) - 1
    },
    maxRenewalDate(): string {
      return dayjs().add(this.renewalPeriod, "days").format(renewalDateFormat)
    },
    minRenewalDate(): string {
      const renewalPeriodMinDate = dayjs().subtract(this.renewalPeriod, "days")
      if (renewalPeriodMinDate.isAfter(this.selectedTariffsLatestFromDate)) {
        return renewalPeriodMinDate.format(renewalDateFormat)
      } else {
        return (
          this.selectedTariffsLatestFromDate?.format(renewalDateFormat) ?? ""
        )
      }
    },
    selectedTariffsLatestFromDate(): Dayjs {
      return Object.values(this.selectedTariffs)
        .map((tariff) => tariff.productDetails.fromDttm)
        .sort((date1, date2) => (dayjs(date1).isAfter(date2) ? -1 : 1))[0]
    },
    quoteTableData(): {
      headers: SoDataTableHeader[]
      items: SoDataTableItem[]
    } {
      const tableData = {
        headers: [
          {
            title: "Current Tariff",
            key: "currentTariff",
            type: SoDataTableColumnType.CHIPS,
            sortable: false,
          },
          {
            title: "→",
            key: "arrow",
            sortable: false,
          },
          {
            title: "Selected Tariff",
            key: "selectedTariff",
            type: SoDataTableColumnType.CHIPS,
            sortable: false,
          },
        ] as SoDataTableHeader[],
        items: Object.entries(this.selectedTariffs).reduce(
          (items, [meterpointId, selectedTariff]) => {
            const currentTariff =
              this.meterpointTariffs[meterpointId].currentTariff

            if (selectedTariff.display && !!currentTariff) {
              items.push({
                currentTariff: {
                  value: currentTariff?.display,
                  props: currentTariff.tariffChips,
                },
                arrow: "→",
                selectedTariff: {
                  value: selectedTariff.display,
                  highlight: currentTariff?.display !== selectedTariff.display,
                  props: selectedTariff.tariffChips,
                },
              })
            }

            if (selectedTariff.display && !currentTariff) {
              items.push({
                currentTariff: {
                  value: "N/A",
                },
                arrow: "→",
                selectedTariff: {
                  value: selectedTariff.display,
                  highlight: true,
                  props: selectedTariff.tariffChips,
                },
              })
            }

            return items
          },
          [] as SoDataTableItem[],
        ),
      }

      tableData.items.push({
        currentTariff: {
          value: `Annual Cost: ${formatMoney({
            amount: this.currentAgreementsCostTotal,
            showNegativeSign: false,
          })}`,
        },
        arrow: "→",
        selectedTariff: {
          value: `Annual Cost: ${formatMoney({
            amount: this.selectedAgreementsCostTotal,
            showNegativeSign: false,
          })}`,
        },
      })

      return tableData
    },
    annualCosts(state) {
      Object.keys(this.registerUsageConverted).length === 0 &&
        this.initialiseMeterpoints()
      return tariffHelper.getAnnualCosts(
        this.meterpointTariffs,
        this.registerUsageConverted,
        state.selectedTariffs,
      )
    },
    currentAgreementsCostTotal() {
      return Object.values(this.annualCosts).reduce(
        (total, meterpointCosts) =>
          total + meterpointCosts.currentAgreementCost,
        0,
      )
    },
    selectedAgreementsCostTotal() {
      return Object.values(this.annualCosts).reduce(
        (total, meterpointCosts) =>
          total + meterpointCosts.selectedAgreementCost,
        0,
      )
    },
    agreementEndDates(): (Dayjs | null)[] {
      return tariffHelper
        .getAgreementEndDates(this.meterpointTariffs)
        .filter((dates) => dates)
    },
    closestAgreementEndDate(): Dayjs {
      const agreementEndDates = this.agreementEndDates

      if (agreementEndDates.length > 0) {
        return dayjs.min(agreementEndDates) ?? dayjs().add(1, "day")
      } else {
        return dayjs().add(1, "day")
      }
    },
    defaultRenewalStartFormatted(): string {
      return this.defaultRenewalStart?.date.format(renewalDateFormat)
    },
    exitFeeTotal(state): number {
      const renewalPeriod = useCurrentAccountStore().renewalPeriod.data
      return tariffHelper.getExitFeeTotal(
        this.meterpointTariffs,
        state.changeDate,
        renewalPeriod,
      )
    },
    currentPayments(): string | undefined {
      const accountNumber = useCurrentAccountStore().accountNumber
      return PaymentScheduleService.getCurrentSchedule(accountNumber).data
        ?.amountFormatted
    },
    currentPaymentsValue(): number | undefined {
      const accountNumber = useCurrentAccountStore().accountNumber
      return PaymentScheduleService.getCurrentSchedule(accountNumber).data
        ?.amount
    },
    defaultRenewalStart(state): {
      date: Dayjs
      alignedTo: RenewalStartAlignmentType
    } {
      return tariffHelper.getDefaultRenewalStart(
        this.meterpointTariffs,
        state.selectedTariffs,
      )
    },
    adjustedAnnualCost(state): number {
      const accountBalance = useCurrentAccountStore().account.data.balance
      const oneOffPayment = Number(state.oneOffPayment)

      const adjustedCost =
        this.selectedAgreementsCostTotal +
        this.exitFeeTotal +
        (state.includeAccountBalance ? accountBalance : 0)
      return adjustedCost - oneOffPayment
    },
    tariffCostsDifference(): number {
      return (
        Number(this.adjustedAnnualCost) -
        Number(this.currentAgreementsCostTotal)
      )
    },
    suggestedPaymentAmount(): number {
      return Number((Number(this.adjustedAnnualCost) / 12).toFixed(2))
    },
    paymentChangeDiff(): number {
      const num =
        Number(this.suggestedPaymentAmount.toFixed(2)) -
        this.currentPaymentsValue
      return Number(num.toFixed(2))
    },
    formattedRegisterUsage(state) {
      const result = {}

      for (const meterpointId in state.registerUsage) {
        const registerId = state.registerUsage[meterpointId]
        const rates = Object.values(registerId)
        const multipleRates = rates.length > 1

        result[meterpointId] = rates
          .map((value) => {
            const usage = value.usage
            const rateName = value.rateName
            return multipleRates && rateName
              ? `${rateName}: ${usage} kWh`
              : `${usage} kWh`
          })
          .join(", ")
      }

      return result
    },
    quoteModalTariffTableData(state) {
      const formatTariff = ({
        productDetails: { standingCharge, earlyTerminationCharge },
        display,
        unitRatesFormatted,
        durationMonths,
        isVariable,
      }) => ({
        tariff: display,
        rateType: isVariable ? "Variable" : "Fixed",
        unitRates: unitRatesFormatted,
        standingCharge: `${formatPriceInPence(standingCharge)}/day`,
        duration: durationMonths
          ? `${durationMonths} months`
          : "Monthly rolling",
        earlyTerminationCharge: formatMoney({ amount: earlyTerminationCharge }),
      })

      let containsVariableTariff = false
      const agreementsChanging: string[][] = []
      const agreementsNotChanging: string[][] = []

      Object.entries(state.selectedTariffs).forEach(
        ([meterpointId, tariff]) => {
          const { meterpoint, activeAgreement } =
            this.meterpointTariffs[meterpointId]
          const formattedMeterpoint = `${meterpoint.meterpointType} - ${meterpoint.identifier}`

          const agreementDetails = {
            meterpoint: formattedMeterpoint,
            estimatedUsage: this.formattedRegisterUsage[meterpointId],
            ...formatTariff(tariff),
          }
          if (activeAgreement) {
            activeAgreement.productName === tariff.display
              ? agreementsNotChanging.push(Object.values(agreementDetails))
              : agreementsChanging.push(Object.values(agreementDetails))

            if (tariff.isVariable) {
              containsVariableTariff = true
            }
          }
        },
      )

      return {
        agreementsChanging,
        agreementsNotChanging,
        containsVariableTariff,
      }
    },
    registerUsageConverted() {
      const convertedUsage = {}

      for (const meterpointId in this.registerUsage) {
        convertedUsage[meterpointId] = {}

        for (const registerId in this.registerUsage[meterpointId]) {
          const register = this.registerUsage[meterpointId][registerId]
          convertedUsage[meterpointId][registerId] = {
            ...register,
            usage: parseFloat(register.usage),
          }
        }
      }

      return convertedUsage
    },
    showRateChangeBanner() {
      return !!FeaturesService.getFeaturesAdminList().data.find(
        (feature: { name: string }) =>
          "UPCOMING_UNIT_RATE_CHANGE" === feature.name,
      )?.enabled
    },
  },
})

export type RenewalsStore = ReturnType<typeof useRenewalsStore>
export type RenewalsTestStore = RemoveReadonly<
  ReturnType<typeof useRenewalsStore>
>
